import { t } from 'i18next';

import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

/**
 * `fetch` rejects with a bare `TypeError: Failed to fetch` when the request never reaches a
 * response: connection dropped mid-flight, answer emitted by the load balancer without the CORS
 * headers Nest adds, DNS or Wi-Fi hiccup. Such a rejection carries no status and no body, so the
 * `ErrorWithDetails` shaping done in `Backend.ts` cannot describe it. Wrapping it gives callers
 * something they can test (`isNetworkError`) to tell "the network failed" from "the backend
 * refused".
 */
export class NetworkError extends Error {
    readonly isNetworkError = true;
    readonly originalError: unknown;

    constructor(originalError: unknown) {
        super('Network request failed');
        this.name = 'NetworkError';
        this.originalError = originalError;
    }
}

type BackendError = {
    message?: string;
    details?: {
        message: string;
        statusCode: number;
    };
    lastResponse?: Response;
    hasBackendMessage?: boolean;
};

/**
 * `lastResponse` is set by `call()` for every failed request, whatever shape the body has.
 * `details.statusCode` only covers Nest's default error body, hence the fallback.
 */
export function getHttpStatus(error: unknown): number | undefined {
    const backendError = error as BackendError;
    return backendError?.lastResponse?.status ?? backendError?.details?.statusCode;
}

/**
 * Only 401 means the session is gone. A 403 carries a business sentence the user needs to read
 * ('You are not allowed to create order for this customer'), so telling them to refresh the page
 * would both be false and hide that sentence.
 */
export function isSessionExpiredError(error: unknown): boolean {
    return getHttpStatus(error) === 401;
}

/**
 * A `fetch` that never reaches a response rejects with a plain `TypeError` whose message is the
 * only thing separating a dropped connection from a programming mistake, and every engine words it
 * differently: Chrome 'Failed to fetch', Firefox 'NetworkError when attempting to fetch resource.'
 * (the trailing dot comes and goes between versions, hence the trimming), Node 'fetch failed', and
 * Safari 'Load failed', which mentions neither fetch nor network.
 */
const FETCH_REJECTION_MESSAGES = [
    'failed to fetch',
    'fetch failed',
    'networkerror when attempting to fetch resource',
    'load failed',
];

/**
 * Matched in full rather than searched for: 'fetch is not a function' or "Cannot read properties
 * of undefined (reading 'fetch')" are bugs in our own code, and telling the user to check their
 * connection would both be false and bury the real cause.
 */
function isBareFetchRejection(error: unknown): boolean {
    return (
        error instanceof TypeError && FETCH_REJECTION_MESSAGES.includes(error.message.toLowerCase().replace(/\.$/, ''))
    );
}

/**
 * True when the request never got an answer: `fetch` rejected instead of resolving. `NetworkError`
 * covers everything going through `call()`, the `isNetworkError` flag catches errors rehydrated
 * across a serialization boundary, which lose their prototype, and the message check catches the
 * raw rejection of a bare `fetch` (login, uploads to S3, the version poll).
 */
export function isNetworkError(error: unknown): boolean {
    return (
        error instanceof NetworkError ||
        (error as { isNetworkError?: boolean })?.isNetworkError === true ||
        isBareFetchRejection(error)
    );
}

/**
 * Some 4xx answers carry a code instead of a sentence ('shopify-instance-not-found',
 * 'Forbidden'), which must never reach a toast. A backend sentence always has a space.
 */
function looksLikeErrorCode(message: string): boolean {
    return !/\s/.test(message);
}

/**
 * The sentence to show the user for a failed request.
 *
 * A 4xx message describes what the caller did and is meant to be read by them. A 5xx message is
 * an internal diagnostic ('Internal error: found no matching warehouse_id'), so it stays behind a
 * generic wording. `t()` is called with the English sentence as key: i18next returns the key
 * itself while the backend catalogue is not imported yet, and starts translating the day it is,
 * without touching a single call site.
 */
export function getDisplayableErrorMessage(error: unknown): string {
    /**
     * No status, no body, nothing the backend said: showing 'Internal error!' here blames the
     * server for what is usually a connection the user can retry, and reads the same as a real 500.
     */
    if (isNetworkError(error)) {
        return t('Connection to the server failed. Please check your connection and try again.');
    }

    const backendError = error as BackendError;
    const status = getHttpStatus(error);
    const message = backendError?.message;

    if (!backendError?.hasBackendMessage || !message || !status || status >= 500 || looksLikeErrorCode(message)) {
        return t('Internal error!');
    }

    return t(message);
}

export function handleError(error: unknown) {
    const backendError = error as BackendError;
    if (isSessionExpiredError(error)) {
        toast.error(t('Expired session. Please refresh this page'));
    } else {
        toast.error(getDisplayableErrorMessage(error));
    }
    console.error(backendError);
}
